import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarCheck, Check, CheckCircle2, ChevronLeft, ChevronRight, Info, Mail, Plus, Search, Upload, UserPlus, Users, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ImportContactsModal } from '@/components/ImportContactsModal';
import { eventService } from '@/lib/api/event.service';
import { guestService } from '@/lib/api/guest.service';
import { userContactService } from '@/lib/api/user-contact.service';
import type { Event } from '@/types/event.types';
import type { Guest } from '@/types/guest.types';
import type { CreateGuestDto } from '@/types/guest.types';
import type { CreateUserContactRequest, UserContact } from '@/types/user-contact.types';

interface AddCelebrationGuestsPanelProps {
  celebrationId: number;
  celebrationName: string;
  defaultEventId?: number;
  onClose: () => void;
  onAdded: () => Promise<void> | void;
  onReviewEventAssignments?: () => void;
}

type PanelStep = 'select' | 'review' | 'done';

const STEP_DEFINITIONS = [
  { id: 'select', label: 'Select contacts' },
  { id: 'review', label: 'Review & assign events' },
  { id: 'done', label: 'Next steps' },
] as const satisfies ReadonlyArray<{ id: PanelStep; label: string }>;

const STEP_ORDER: PanelStep[] = STEP_DEFINITIONS.map((definition) => definition.id);

interface SaveSummary {
  guestsAdded: number;
  assignmentsAdded: number;
}

interface ExistingGuestSummary {
  userId: number;
  eventIds: Set<number>;
  hostName?: string;
  hostNickname?: string;
  creatorName?: string;
  creatorNickname?: string;
}

type ReviewCategory = 'new' | 'already_added' | 'possible_duplicate' | 'missing_info';

interface ReviewContactEntry {
  contact: UserContact;
  existingGuest?: ExistingGuestSummary;
  needsCelebrationAdd: boolean;
  missingEventIds: number[];
  hasChanges: boolean;
  category: ReviewCategory;
  duplicateOfName?: string;
}

const REVIEW_CATEGORY_LABELS: Record<ReviewCategory, string> = {
  new: 'New',
  already_added: 'Already added',
  possible_duplicate: 'Possible duplicate',
  missing_info: 'Missing info',
};

const normalizeName = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, ' ');

const PAGE_SIZE = 100;

export const AddCelebrationGuestsPanel: React.FC<AddCelebrationGuestsPanelProps> = ({
  celebrationId,
  celebrationName,
  defaultEventId,
  onClose,
  onAdded,
  onReviewEventAssignments,
}) => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<UserContact[]>([]);
  const [celebrationGuests, setCelebrationGuests] = useState<Guest[]>([]);
  const [celebrationEvents, setCelebrationEvents] = useState<Event[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [selectedContactsByUserId, setSelectedContactsByUserId] = useState<Record<number, UserContact>>({});
  const [selectedEventIds, setSelectedEventIds] = useState<Set<number>>(new Set());
  const [step, setStep] = useState<PanelStep>('select');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingContext, setLoadingContext] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [defaultEventsApplied, setDefaultEventsApplied] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [addContactForm, setAddContactForm] = useState<CreateUserContactRequest>({ full_name: '', email: '', phone: '', relationship: '' });
  const [savingNewContact, setSavingNewContact] = useState(false);
  const [saveSummary, setSaveSummary] = useState<SaveSummary | null>(null);

  const existingGuestsByUserId = useMemo(() => {
    const guestMap = new Map<number, ExistingGuestSummary>();
    for (const guest of celebrationGuests) {
      if (!guest.user_id) continue;
      const existingGuest = guestMap.get(guest.user_id);
      if (existingGuest) {
        if (guest.event_id) {
          existingGuest.eventIds.add(guest.event_id);
        }
        continue;
      }

      guestMap.set(guest.user_id, {
        userId: guest.user_id,
        eventIds: new Set(guest.event_id ? [guest.event_id] : []),
        hostName: guest.host_name || guest.creator_name,
        hostNickname: guest.host_nickname || guest.creator_nickname,
        creatorName: guest.creator_name,
        creatorNickname: guest.creator_nickname,
      });
    }
    return guestMap;
  }, [celebrationGuests]);

  const selectedContacts = useMemo(
    () =>
      Array.from(selectedUserIds)
        .map((userId) => selectedContactsByUserId[userId])
        .filter((contact): contact is UserContact => Boolean(contact)),
    [selectedContactsByUserId, selectedUserIds],
  );

  const selectedEventList = useMemo(
    () => celebrationEvents.filter((event) => selectedEventIds.has(event.id)),
    [celebrationEvents, selectedEventIds],
  );

  const reviewEntries = useMemo<ReviewContactEntry[]>(() => {
    // Name collisions across distinct user ids are the only duplicate signal available here.
    const nameCounts = new Map<string, number>();
    for (const guest of celebrationGuests) {
      if (!guest.name) continue;
      const key = normalizeName(guest.name);
      nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
    }

    return selectedContacts.map((contact) => {
      const existingGuest = existingGuestsByUserId.get(contact.user_id);
      const missingEventIds = Array.from(selectedEventIds).filter(
        (eventId) => !existingGuest?.eventIds.has(eventId),
      );
      const hasContactMethod = Boolean(contact.email?.trim() || contact.phone?.trim());
      const duplicateMatch = !existingGuest && nameCounts.has(normalizeName(contact.full_name));

      const category: ReviewCategory = existingGuest
        ? 'already_added'
        : !hasContactMethod
          ? 'missing_info'
          : duplicateMatch
            ? 'possible_duplicate'
            : 'new';

      return {
        contact,
        existingGuest,
        needsCelebrationAdd: !existingGuest,
        missingEventIds,
        hasChanges: !existingGuest || missingEventIds.length > 0,
        category,
        duplicateOfName: duplicateMatch ? contact.full_name : undefined,
      };
    });
  }, [celebrationGuests, existingGuestsByUserId, selectedContacts, selectedEventIds]);

  const reviewCategoryCounts = useMemo(() => {
    const counts: Record<ReviewCategory, number> = { new: 0, already_added: 0, possible_duplicate: 0, missing_info: 0 };
    for (const entry of reviewEntries) {
      counts[entry.category] += 1;
    }
    return counts;
  }, [reviewEntries]);

  const actionableReviewEntries = useMemo(
    () => reviewEntries.filter((entry) => entry.hasChanges),
    [reviewEntries],
  );

  const assignmentCount = useMemo(
    () => actionableReviewEntries.reduce((total, entry) => total + entry.missingEventIds.length, 0),
    [actionableReviewEntries],
  );

  const loadContacts = useCallback(async () => {
    setLoadingContacts(true);
    setError(null);

    try {
      // search-all ignores limit/offset and returns every match; page client-side.
      const response = await userContactService.searchContacts({ search: searchTerm });
      setContacts(response.contacts.filter((contact) => Number.isInteger(contact.user_id) && contact.user_id > 0));
    } catch (loadError) {
      console.error('Failed to load contacts for celebration guests:', loadError);
      setError('Unable to load contacts. Please try again.');
    } finally {
      setLoadingContacts(false);
    }
  }, [searchTerm]);

  const loadContext = useCallback(async () => {
    setLoadingContext(true);
    try {
      const [guests, allEvents] = await Promise.all([
        guestService.getCelebrationGuests(celebrationId),
        eventService.getEvents({ celebrationId }),
      ]);
      setCelebrationGuests(guests);
      setCelebrationEvents(allEvents.filter((event) => !event.deleted_at));
    } catch (loadError) {
      console.error('Failed to load add guest context:', loadError);
      setError('Unable to load celebration guest details. Please try again.');
    } finally {
      setLoadingContext(false);
    }
  }, [celebrationId]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  useEffect(() => {
    if (defaultEventsApplied || celebrationEvents.length === 0) return;

    const nextSelectedEventIds = new Set<number>();
    if (defaultEventId && celebrationEvents.some((event) => event.id === defaultEventId)) {
      nextSelectedEventIds.add(defaultEventId);
    } else if (celebrationEvents.length === 1) {
      nextSelectedEventIds.add(celebrationEvents[0].id);
    }

    setSelectedEventIds(nextSelectedEventIds);
    setDefaultEventsApplied(true);
  }, [celebrationEvents, defaultEventId, defaultEventsApplied]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, saving]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const toggleContact = (contact: UserContact, checked: boolean) => {
    setSelectedUserIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(contact.user_id);
      } else {
        next.delete(contact.user_id);
      }
      return next;
    });

    setSelectedContactsByUserId((previous) => {
      if (checked) {
        return { ...previous, [contact.user_id]: contact };
      }

      const { [contact.user_id]: _removedContact, ...remainingContacts } = previous;
      return remainingContacts;
    });
  };

  const toggleEvent = (eventId: number, checked: boolean) => {
    setSelectedEventIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(eventId);
      } else {
        next.delete(eventId);
      }
      return next;
    });
  };

  const handleImportSuccess = async () => {
    setShowImportModal(false);
    setCurrentPage(1);
    await loadContacts();
  };

  const resetAddContactForm = () => {
    setAddContactForm({ full_name: '', email: '', phone: '', relationship: '' });
    setShowAddContactForm(false);
  };

  const handleAddContactSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!addContactForm.full_name.trim()) {
      setError('Full name is required to add a contact.');
      return;
    }
    if (!addContactForm.email?.trim() && !addContactForm.phone?.trim()) {
      setError('Add an email or phone number for the new contact.');
      return;
    }

    setSavingNewContact(true);
    setError(null);

    try {
      const newContact = await userContactService.createContact(addContactForm);
      toggleContact(newContact, true);
      resetAddContactForm();
      setCurrentPage(1);
      await loadContacts();
    } catch (createError) {
      console.error('Failed to add new contact:', createError);
      setError('Unable to add this contact. Please try again.');
    } finally {
      setSavingNewContact(false);
    }
  };

  const getContactStatusLabel = (contact: UserContact): string => {
    const existingGuest = existingGuestsByUserId.get(contact.user_id);
    if (!existingGuest) {
      return 'New to celebration';
    }

    if (defaultEventId && existingGuest.eventIds.has(defaultEventId)) {
      return 'Already in this event';
    }

    return 'Already in celebration';
  };

  const getContactStatusContext = (contact: UserContact): string | null => {
    const existingGuest = existingGuestsByUserId.get(contact.user_id);
    if (!existingGuest) {
      return null;
    }

    if (existingGuest.hostName && existingGuest.creatorName && existingGuest.creatorName !== existingGuest.hostName) {
      return `Host: ${existingGuest.hostName} · Added by ${existingGuest.creatorName}`;
    }

    if (existingGuest.hostName) {
      return `Host: ${existingGuest.hostName}`;
    }

    if (existingGuest.creatorName) {
      return `Added by ${existingGuest.creatorName}`;
    }

    return null;
  };

  const handleContinueToReview = () => {
    if (selectedUserIds.size === 0) return;
    setStep('review');
  };

  const handleAddGuests = async () => {
    if (selectedUserIds.size === 0) return;

    setSaving(true);
    setError(null);

    try {
      const guestsToCreate: CreateGuestDto[] = actionableReviewEntries
        .filter((entry) => entry.needsCelebrationAdd)
        .map((entry) => ({
          celebration_id: celebrationId,
          user_id: entry.contact.user_id,
          user_contact_id: entry.contact.id,
          dietary_restrictions: entry.contact.dietary_preferences,
          rsvp_status: 'new',
        }));

      const eventAssignments = actionableReviewEntries.flatMap((entry) =>
        entry.missingEventIds.map((eventId) => ({
          clientChangeId: `assign-${entry.contact.user_id}-${eventId}`,
          action: 'assign' as const,
          event_id: eventId,
          user_id: entry.contact.user_id,
        })),
      );

      if (guestsToCreate.length === 0 && eventAssignments.length === 0) {
        setError('Selected contacts are already added with the chosen event assignments.');
        return;
      }

      if (guestsToCreate.length > 0) {
        await guestService.addCelebrationGuests(celebrationId, guestsToCreate);
      }

      let eventAssignmentError: string | null = null;
      if (eventAssignments.length > 0) {
        const eventAssignmentResponse = await guestService.bulkUpdateGuestEvents(
          celebrationId,
          eventAssignments,
        );

        if (eventAssignmentResponse.status !== 'success') {
          const failedCount = eventAssignmentResponse.summary.failed;
          eventAssignmentError = failedCount === 1
            ? '1 event assignment needs review. Guests were still added to the celebration.'
            : `${failedCount} event assignments need review. Guests were still added to the celebration.`;
        }
      }

      await onAdded();

      if (eventAssignmentError) {
        await loadContext();
        setError(eventAssignmentError);
        return;
      }

      setSaveSummary({ guestsAdded: guestsToCreate.length, assignmentsAdded: eventAssignments.length });
      setStep('done');
    } catch (saveError) {
      console.error('Failed to add contacts to celebration:', saveError);
      setError('Unable to add selected contacts. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const loading = loadingContacts || loadingContext;

  const handleAddMoreGuests = async () => {
    setSelectedUserIds(new Set());
    setSelectedContactsByUserId({});
    setSaveSummary(null);
    setError(null);
    setCurrentPage(1);
    setStep('select');
    await loadContext();
    await loadContacts();
  };
  const visibleContacts = useMemo(
    () =>
      contacts.filter((contact) => {
        const existingGuest = existingGuestsByUserId.get(contact.user_id);
        if (!existingGuest) return true;
        // Existing guests stay selectable while a target event assignment is still missing.
        return Boolean(defaultEventId) && !existingGuest.eventIds.has(defaultEventId as number);
      }),
    [contacts, defaultEventId, existingGuestsByUserId],
  );
  const pagedContacts = useMemo(
    () => visibleContacts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [currentPage, visibleContacts],
  );
  const totalPages = Math.max(1, Math.ceil(visibleContacts.length / PAGE_SIZE));
  const showingFrom = visibleContacts.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(currentPage * PAGE_SIZE, visibleContacts.length);

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="add-celebration-guests-title">
      <button
        type="button"
        className="absolute inset-0 w-full cursor-default bg-black/30"
        aria-label="Close add guests panel"
        onClick={saving ? undefined : onClose}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border bg-white shadow-xl">
        <header className="flex items-start justify-between gap-4 border-b px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h2 id="add-celebration-guests-title" className="text-base font-semibold text-foreground">
                Add guests
              </h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {defaultEventId
                  ? 'Select contacts, then review celebration add + event assignment in one flow.'
                  : `Select contacts to add to ${celebrationName || 'this celebration'}.`}
              </p>
            </div>
          </div>
          <Button size="icon" variant="ghost" onClick={onClose} disabled={saving} aria-label="Close add guests panel">
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-4 p-5">
          <ol className="flex items-center gap-2 rounded-lg border bg-muted/20 p-2 text-sm">
            {STEP_DEFINITIONS.map((panelStep, index) => {
              const currentIndex = STEP_ORDER.indexOf(step);
              const isActive = step === panelStep.id;
              const isComplete = index < currentIndex;

              return (
                <li
                  key={panelStep.id}
                  aria-current={isActive ? 'step' : undefined}
                  className={`flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 ${
                    isActive
                      ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
                      : isComplete
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                      isActive
                        ? 'bg-primary text-white'
                        : isComplete
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {isComplete ? <Check className="h-3 w-3" /> : index + 1}
                  </span>
                  <span className={`truncate text-xs sm:text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>
                    {panelStep.label}
                  </span>
                </li>
              );
            })}
          </ol>

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert">
              {error}
            </div>
          )}

          {step === 'select' ? (
            <>
              <div className="flex flex-wrap items-center gap-2">                <Button type="button" variant="outline" size="sm" onClick={() => setShowImportModal(true)} disabled={saving}>
                  <Upload className="mr-1 h-4 w-4" />
                  Import CSV
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowAddContactForm((current) => !current)} disabled={saving}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add contact
                </Button>
              </div>

              {showAddContactForm && (
                <form onSubmit={handleAddContactSubmit} className="space-y-3 rounded-md border p-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="new-contact-full-name">Full name *</Label>
                      <Input
                        id="new-contact-full-name"
                        value={addContactForm.full_name}
                        onChange={(event) => setAddContactForm((current) => ({ ...current, full_name: event.target.value }))}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-contact-email">Email</Label>
                      <Input
                        id="new-contact-email"
                        type="email"
                        value={addContactForm.email}
                        onChange={(event) => setAddContactForm((current) => ({ ...current, email: event.target.value }))}
                        placeholder="Email address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-contact-phone">Phone</Label>
                      <Input
                        id="new-contact-phone"
                        type="tel"
                        value={addContactForm.phone}
                        onChange={(event) => setAddContactForm((current) => ({ ...current, phone: event.target.value }))}
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-contact-relationship">Relationship</Label>
                      <Input
                        id="new-contact-relationship"
                        value={addContactForm.relationship}
                        onChange={(event) => setAddContactForm((current) => ({ ...current, relationship: event.target.value }))}
                        placeholder="e.g., Friend, Family"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={resetAddContactForm} disabled={savingNewContact}>
                      Cancel
                    </Button>
                    <Button type="submit" size="sm" disabled={savingNewContact}>
                      {savingNewContact ? 'Adding...' : 'Add & select'}
                    </Button>
                  </div>
                </form>
              )}

              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(event) => handleSearchChange(event.target.value)}
                  placeholder="Search contacts by name, email, or phone"
                  className="pl-9"
                  aria-label="Search contacts"
                />
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
                {loading ? (
                  <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">Loading contacts...</div>
                ) : contacts.length === 0 ? (
                  <div className="flex h-40 flex-col items-center justify-center gap-3 px-6 text-center">
                    <p className="text-sm text-muted-foreground">No contacts found.</p>
                    <Button variant="outline" size="sm" onClick={() => navigate('/contacts')}>
                      Manage contacts
                    </Button>
                  </div>
                ) : visibleContacts.length === 0 ? (
                  <div className="flex h-40 flex-col items-center justify-center gap-3 px-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      {defaultEventId
                        ? 'All your contacts are already assigned to this event.'
                        : 'All your contacts are already added to this celebration.'}
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y">
                    {pagedContacts.map((contact) => {
                      const isSelected = selectedUserIds.has(contact.user_id);
                      const statusContext = getContactStatusContext(contact);

                      return (
                        <li key={contact.id} className="flex items-center gap-3 px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={saving}
                            onChange={(event) => toggleContact(contact, event.target.checked)}
                            aria-label={`Select ${contact.full_name}`}
                            className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="truncate text-sm font-medium text-foreground">{contact.full_name}</p>
                              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                                {getContactStatusLabel(contact)}
                              </span>
                            </div>
                            <p className="truncate text-xs text-muted-foreground">{contact.email || contact.phone || 'No email or phone'}</p>
                            {statusContext ? (
                              <p className="truncate text-[11px] text-muted-foreground">{statusContext}</p>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Showing {showingFrom}-{showingTo} of {visibleContacts.length}</span>
                <div className="flex items-center gap-3">
                  <span>{selectedUserIds.size} selected</span>
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentPage === 1 || loading}
                      aria-label="Previous contacts page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="min-w-16 text-center">{currentPage} / {totalPages}</span>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={currentPage === totalPages || loading}
                      aria-label="Next contacts page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : step === 'review' ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
              <section className="rounded-lg border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Event assignment</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {celebrationEvents.length <= 1
                        ? 'Single-event celebrations are selected by default.'
                        : defaultEventId
                          ? 'This event was preselected because you launched Add guests from an event page.'
                          : 'Choose which events should be assigned during add.'}
                    </p>
                  </div>
                  {selectedEventList.length > 0 ? (
                    <span className="text-xs text-muted-foreground">{selectedEventList.length} selected</span>
                  ) : null}
                </div>

                <div className="mt-3 space-y-2">
                  {celebrationEvents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No events are available yet. Guests will be added at the celebration level only.</p>
                  ) : celebrationEvents.map((event) => (
                    <label key={event.id} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedEventIds.has(event.id)}
                        onChange={(checkboxEvent) => toggleEvent(event.id, checkboxEvent.target.checked)}
                        disabled={saving}
                        aria-label={`Assign ${event.title}`}
                        className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
                      />
                      <span>{event.title}</span>
                    </label>
                  ))}
                </div>
              </section>

              <section className="rounded-lg border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Review selected guests</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {actionableReviewEntries.length} guest{actionableReviewEntries.length === 1 ? '' : 's'} will change · {assignmentCount} event assignment{assignmentCount === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {(Object.keys(REVIEW_CATEGORY_LABELS) as ReviewCategory[])
                    .filter((category) => reviewCategoryCounts[category] > 0)
                    .map((category) => (
                      <span
                        key={category}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                          category === 'new'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : category === 'already_added'
                              ? 'border-gray-200 bg-gray-100 text-gray-600'
                              : category === 'possible_duplicate'
                                ? 'border-amber-200 bg-amber-50 text-amber-700'
                                : 'border-destructive/30 bg-destructive/5 text-destructive'
                        }`}
                      >
                        {REVIEW_CATEGORY_LABELS[category]}: {reviewCategoryCounts[category]}
                      </span>
                    ))}
                </div>

                <ul className="mt-3 space-y-3">
                  {reviewEntries.map((entry) => {
                    const eventNames = entry.missingEventIds
                      .map((eventId) => celebrationEvents.find((event) => event.id === eventId)?.title)
                      .filter((eventTitle): eventTitle is string => Boolean(eventTitle));

                    return (
                      <li key={entry.contact.id} className="rounded-md border px-3 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">{entry.contact.full_name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {entry.contact.email || entry.contact.phone || 'No email or phone'}
                            </p>
                          </div>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            entry.category === 'new'
                              ? 'bg-emerald-50 text-emerald-700'
                              : entry.category === 'possible_duplicate'
                                ? 'bg-amber-50 text-amber-700'
                                : entry.category === 'missing_info'
                                  ? 'bg-destructive/5 text-destructive'
                                  : 'bg-muted text-muted-foreground'
                          }`}>
                            {REVIEW_CATEGORY_LABELS[entry.category]}
                          </span>
                        </div>
                        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                          {entry.category === 'possible_duplicate' ? (
                            <p className="text-amber-700">Someone with this name is already on the guest list. Confirm this is a different person.</p>
                          ) : null}
                          {entry.category === 'missing_info' ? (
                            <p className="text-destructive">No email or phone. They cannot receive an invitation until you add one.</p>
                          ) : null}
                          <p>{entry.needsCelebrationAdd ? 'Will be added to the celebration.' : 'Already in this celebration.'}</p>
                          {eventNames.length > 0 ? (
                            <p>Will be assigned to: {eventNames.join(', ')}.</p>
                          ) : selectedEventIds.size > 0 ? (
                            <p>Already assigned to the selected event choices.</p>
                          ) : (
                            <p>No event assignments selected during this add flow.</p>
                          )}
                          {entry.existingGuest?.hostName ? (
                            <p>
                              Host: {entry.existingGuest.hostName}
                              {entry.existingGuest.creatorName && entry.existingGuest.creatorName !== entry.existingGuest.hostName
                                ? ` · Added by ${entry.existingGuest.creatorName}`
                                : ''}
                            </p>
                          ) : entry.existingGuest?.creatorName ? (
                            <p>Added by {entry.existingGuest.creatorName}</p>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
              <section className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  <div>
                    <h3 className="text-sm font-semibold text-emerald-900">Guests added to this celebration.</h3>
                    <p className="mt-1 text-sm text-emerald-800">
                      {saveSummary?.guestsAdded
                        ? `${saveSummary.guestsAdded} guest${saveSummary.guestsAdded === 1 ? '' : 's'} added to ${celebrationName || 'this celebration'}.`
                        : 'Your selected contacts are up to date on this celebration.'}
                      {saveSummary?.assignmentsAdded
                        ? ` ${saveSummary.assignmentsAdded} event assignment${saveSummary.assignmentsAdded === 1 ? '' : 's'} saved.`
                        : ''}
                    </p>
                  </div>
                </div>
              </section>

              <section className="rounded-lg border p-4">
                <h3 className="text-sm font-semibold text-foreground">What to do next</h3>
                <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CalendarCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span>
                      {celebrationEvents.length <= 1
                        ? 'They are added to the event. RSVP invitations are not sent yet.'
                        : 'Review Event Assignments to choose which events each guest should attend.'}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Mail className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span>Send invitations from the Invitations page when your list is ready.</span>
                  </li>
                </ul>
              </section>

              <section className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                <div className="flex items-start gap-3">
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <p className="text-sm text-amber-900">
                    Adding guests does not send RSVP invitations automatically. Go to Invitations when you are ready to send.
                  </p>
                </div>
              </section>
            </div>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-4">
          {step === 'done' ? (
            <>
              <Button variant="outline" size="sm" onClick={handleAddMoreGuests} disabled={saving}>
                <UserPlus className="mr-1 h-4 w-4" />
                Add more guests
              </Button>
              <div className="flex flex-wrap items-center gap-2">
                {celebrationEvents.length > 1 && onReviewEventAssignments ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      onReviewEventAssignments();
                      onClose();
                    }}
                  >
                    <CalendarCheck className="mr-1 h-4 w-4" />
                    Review event assignments
                  </Button>
                ) : null}
                <Button variant="outline" onClick={() => navigate(`/celebrations/${celebrationId}/invitations`)}>
                  <Mail className="mr-1 h-4 w-4" />
                  Go to invitations
                </Button>
                <Button onClick={onClose}>Done</Button>
              </div>
            </>
          ) : (
            <>
              {step === 'review' ? (
                <Button variant="outline" size="sm" onClick={() => setStep('select')} disabled={saving}>
                  Back to contacts
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => navigate('/contacts')}>
                  Manage contacts
                </Button>
              )}
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                {step === 'select' ? (
                  <Button onClick={handleContinueToReview} disabled={selectedUserIds.size === 0 || saving || loading}>
                    Continue to review
                  </Button>
                ) : (
                  <Button onClick={handleAddGuests} disabled={actionableReviewEntries.length === 0 || saving}>
                    <UserPlus className="mr-1 h-4 w-4" />
                    {saving ? 'Saving...' : `Save guests${actionableReviewEntries.length ? ` (${actionableReviewEntries.length})` : ''}`}
                  </Button>
                )}
              </div>
            </>
          )}
        </footer>
        </div>
      </div>

      <ImportContactsModal
        isOpen={showImportModal}
        celebrationId={undefined}
        onClose={() => setShowImportModal(false)}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
};